import type { ReactNode } from 'react';

import { classes } from '../lib/divers';

/** Conteneur de section. */
export function Carte({
  titre,
  sousTitre,
  actions,
  children,
  sansPadding = false,
  className,
}: {
  titre?: ReactNode;
  sousTitre?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  sansPadding?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <section className={classes('gv-carte', className)}>
      {titre !== undefined ? (
        <header className="gv-carte__entete">
          <div className="gv-pile gv-pile--serree" style={{ minWidth: 0 }}>
            <h2 className="gv-h3">{titre}</h2>
            {sousTitre !== undefined ? (
              <span className="gv-corps-s gv-secondaire">{sousTitre}</span>
            ) : null}
          </div>
          {actions ? <div className="gv-carte__entete-actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={classes('gv-carte__corps', sansPadding && 'gv-carte__corps--nu')}>
        {children}
      </div>
    </section>
  );
}

export type TonTuile = 'neutre' | 'vert' | 'orange' | 'rouge' | 'bleu';

/**
 * Tuile de statistique : un chiffre, son unité, une note de contexte.
 *
 * Pas de graphe ici — quand la donnée tient en un nombre, un nombre est la meilleure
 * visualisation possible. La couleur n'est employée que lorsqu'elle porte un état
 * (rouge = il faut agir), jamais pour décorer.
 */
export function Tuile({
  libelle,
  valeur,
  unite,
  note,
  ton = 'neutre',
  action,
}: {
  libelle: string;
  valeur: ReactNode;
  unite?: string;
  note?: ReactNode;
  ton?: TonTuile;
  action?: ReactNode;
}): JSX.Element {
  return (
    <section className={classes('gv-carte', ton !== 'neutre' && `gv-tuile--${ton}`)}>
      <div className="gv-tuile">
        <span className="gv-label">{libelle}</span>
        <p className="gv-tuile__valeur">
          {valeur}
          {unite ? <span className="gv-tuile__unite">{unite}</span> : null}
        </p>
        {note !== undefined ? <span className="gv-tuile__note">{note}</span> : null}
        {action ? <div style={{ marginTop: 'var(--gv-esp-2)' }}>{action}</div> : null}
      </div>
    </section>
  );
}

/** Liste de paires clé/valeur (fiche technique, paramétrage). */
export function Definitions({
  entrees,
}: {
  entrees: ReadonlyArray<{ cle: string; valeur: ReactNode }>;
}): JSX.Element {
  return (
    <dl className="gv-defs">
      {entrees.map((e) => (
        <div key={e.cle}>
          <dt className="gv-defs__cle">{e.cle}</dt>
          <dd className="gv-defs__valeur">{e.valeur}</dd>
        </div>
      ))}
    </dl>
  );
}
